import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import ChapterCard from "@/components/feature/ChapterCard";
import { chapters } from "@/data/chapters";
import { useFavorites } from "@/hooks/useFavorites";
import { usePageMeta } from "@/hooks/usePageMeta";
import FilterSidebar, { type SegmentFilter } from "./components/FilterSidebar";
import FilterDrawer, { type SortValue } from "./components/FilterDrawer";
import Pagination from "./components/Pagination";

const PAGE_SIZE = 6;

export default function Category() {
  usePageMeta({
    title: "色戒无删减版电影章节分类浏览 - 电影完整版平台",
    description:
      "按剧情时间线浏览色戒无删减版全部 12 个章节，支持剧情关键词搜索、时间线分段筛选、播放时长排序与本地收藏查看，快速定位你想了解的剧情段落。",
    keywords: "色戒章节分类,色戒剧情章节,色戒剧情筛选,色戒章节列表,色戒无删减版剧情",
  });

  const { favorites } = useFavorites();
  const [keyword, setKeyword] = useState("");
  const [segment, setSegment] = useState<SegmentFilter>("全部");
  const [sort, setSort] = useState<SortValue>("default");
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    const query = keyword.trim().toLowerCase();
    const list = chapters.filter((chapter) => {
      const matchSegment = segment === "全部" || chapter.segment === segment;
      const matchFavorite = !onlyFavorites || favorites.includes(chapter.id);
      const matchKeyword =
        !query ||
        chapter.title.toLowerCase().includes(query) ||
        chapter.excerpt.toLowerCase().includes(query) ||
        chapter.tags.some((tag) => tag.toLowerCase().includes(query));
      return matchSegment && matchFavorite && matchKeyword;
    });

    if (sort === "asc") return [...list].sort((a, b) => a.duration - b.duration);
    if (sort === "desc") return [...list].sort((a, b) => b.duration - a.duration);
    return list;
  }, [keyword, segment, sort, onlyFavorites, favorites]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [keyword, segment, sort, onlyFavorites]);

  const resetAll = () => {
    setKeyword("");
    setSegment("全部");
    setSort("default");
    setOnlyFavorites(false);
    setPage(1);
  };

  const hasFilters = keyword.trim() !== "" || segment !== "全部" || onlyFavorites;

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main>
        <section className="relative overflow-hidden border-b border-background-200 pb-10 pt-24 md:pb-14 md:pt-36">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=dark%20cinema%20hall%20with%20rows%20of%20empty%20velvet%20seats%20and%20a%20single%20warm%20spotlight%20on%20a%20red%20curtain%2C%20deep%20burgundy%20and%20amber%20tones%2C%20atmospheric%20haze%2C%20cinematic%20wide%20composition%2C%20no%20text&width=1920&height=700&seq=sj-category-band&orientation=landscape"
              alt="章节分类页背景"
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-background-50/90" />
            <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/40" />
          </div>

          <div className="container-page relative">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "章节分类" }]} />
            <h1 className="mt-5 font-heading text-3xl text-foreground-950 md:text-[40px]">
              色戒无删减版<span className="text-accent-500">电影章节浏览</span>
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-[15px]">
              按剧情时间线整理的 12 个章节，每一章都有独立的封面、摘要与完整解说。用关键词搜索某句台词、某个场景，或直接按时间线阶段筛选。
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 font-label text-xs text-foreground-600">
              <span className="flex items-center gap-1.5">
                <i className="ri-stack-line text-sm leading-none text-accent-500" />共 {chapters.length} 个章节
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line text-sm leading-none text-accent-500" />单章 8 - 18 分钟
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-bookmark-3-line text-sm leading-none text-accent-500" />已收藏 {favorites.length} 章
              </span>
            </div>
          </div>
        </section>

        <section className="container-page py-8 md:py-12">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_1fr] lg:gap-10">
            <FilterSidebar
              segment={segment}
              onSegmentChange={setSegment}
              onlyFavorites={onlyFavorites}
              onToggleFavorites={() => setOnlyFavorites((prev) => !prev)}
              favoriteCount={favorites.length}
            />

            <div className="min-w-0">
              <div className="surface-card flex flex-col gap-3 p-3 sm:flex-row sm:items-center md:p-4">
                <div className="relative min-w-0 flex-1">
                  <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base leading-none text-foreground-500" />
                  <input
                    type="search"
                    value={keyword}
                    onChange={(event) => setKeyword(event.target.value)}
                    placeholder="搜索剧情关键词，如：戒指、牌局、雨夜"
                    aria-label="搜索剧情关键词"
                    className="box-border w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-3 font-label text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-accent-500 focus:outline-none"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <div className="relative hidden sm:block">
                    <select
                      value={sort}
                      onChange={(event) => setSort(event.target.value as SortValue)}
                      aria-label="按时长排序"
                      className="box-border w-full cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 py-2.5 pl-3 pr-9 font-label text-xs text-foreground-800 focus:border-accent-500 focus:outline-none"
                    >
                      <option value="default">按剧情顺序</option>
                      <option value="asc">时长从短到长</option>
                      <option value="desc">时长从长到短</option>
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-base leading-none text-foreground-500" />
                  </div>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(true)}
                    className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 font-label text-xs text-foreground-800 sm:hidden"
                  >
                    <i className="ri-equalizer-line text-base leading-none text-accent-500" />
                    筛选 / 排序
                  </button>
                </div>
              </div>

              <div className="mt-5 flex flex-wrap items-center gap-2">
                <p className="font-label text-xs text-foreground-500">
                  共找到 <span className="text-accent-400">{filtered.length}</span> 个章节
                  {hasFilters && "（已应用筛选条件）"}
                </p>
                {segment !== "全部" && (
                  <button
                    type="button"
                    onClick={() => setSegment("全部")}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-accent-500/40 bg-accent-500/10 px-2.5 py-1 font-label text-[11px] text-accent-400"
                  >
                    {segment}
                    <i className="ri-close-line text-xs leading-none" />
                  </button>
                )}
                {onlyFavorites && (
                  <button
                    type="button"
                    onClick={() => setOnlyFavorites(false)}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-accent-500/40 bg-accent-500/10 px-2.5 py-1 font-label text-[11px] text-accent-400"
                  >
                    只看收藏
                    <i className="ri-close-line text-xs leading-none" />
                  </button>
                )}
                {keyword.trim() && (
                  <button
                    type="button"
                    onClick={() => setKeyword("")}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-accent-500/40 bg-accent-500/10 px-2.5 py-1 font-label text-[11px] text-accent-400"
                  >
                    “{keyword.trim()}”
                    <i className="ri-close-line text-xs leading-none" />
                  </button>
                )}
                {hasFilters && (
                  <button
                    type="button"
                    onClick={resetAll}
                    className="cursor-pointer font-label text-[11px] text-foreground-500 underline decoration-foreground-500/40 underline-offset-4 hover:text-accent-400"
                  >
                    重置筛选
                  </button>
                )}
              </div>

              {paged.length > 0 ? (
                <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 xl:grid-cols-3">
                  {paged.map((chapter) => (
                    <ChapterCard key={chapter.id} chapter={chapter} />
                  ))}
                </div>
              ) : (
                <div className="surface-card mt-4 flex flex-col items-center justify-center px-6 py-16 text-center">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full border border-accent-500/30 bg-accent-500/10 text-accent-400">
                    <i className="ri-file-search-line text-2xl leading-none" />
                  </span>
                  <h2 className="mt-5 font-heading text-lg text-foreground-950">
                    暂无符合条件的章节，您可以重置筛选
                  </h2>
                  <p className="mt-2 max-w-md text-sm text-foreground-600">
                    试试换一个关键词，比如“戒指”“牌局”“雨夜”，或者把时间线切换为“全部”。
                  </p>
                  <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                    <button
                      type="button"
                      onClick={resetAll}
                      className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-medium text-background-50"
                    >
                      重置筛选条件
                    </button>
                    <Link
                      to="/"
                      className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 font-label text-sm text-foreground-800 hover:border-accent-500 hover:text-accent-400"
                    >
                      返回首页
                    </Link>
                  </div>
                </div>
              )}

              <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      <FilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        segment={segment}
        onSegmentChange={setSegment}
        sort={sort}
        onSortChange={setSort}
        onlyFavorites={onlyFavorites}
        onToggleFavorites={() => setOnlyFavorites((prev) => !prev)}
        onReset={resetAll}
        resultCount={filtered.length}
      />
    </div>
  );
}